import { FormEvent, startTransition, useRef } from 'react';
import { useFormStatus } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  FilledInput,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  Link,
} from '@mui/material';
import { AlternateEmail, Person } from '@mui/icons-material';

import { ResetPasswordFields } from './ResetPasswordPage';
import { State } from '@/types';
import { FrontPage } from './FrontPage';

import './ResetPassword.css';

function Submit() {
  const { pending } = useFormStatus();
  const { t } = useTranslation();

  return (
    <Button
      className="reset-password-page-submit-button"
      type="submit"
      fullWidth
      variant="contained"
      disabled={pending}
      data-qa-element-id="resend_password_button"
    >
      {t('login.resetPassword.resendPassword')}
    </Button>
  );
}

function ResetPassword({
  state,
  formAction,
  returnLoginPage,
  isPhone,
}: {
  state: State<ResetPasswordFields>;
  formAction: (payload: FormData) => void;
  returnLoginPage: () => void;
  isPhone: boolean;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLFormElement | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement> | undefined) {
    event?.preventDefault();
    const formData = new FormData(ref.current!);
    startTransition(() => formAction(formData));
  }

  return (
    <>
      {!isPhone && (
        <div className="login-page-right-title">
          <h1 className="reset-password-page-title-forgot" data-qa-element-id="reset_password_view_title">
            {t('login.resetPassword.newPassword')}
          </h1>
          <span data-qa-element-id="provide_username_and_email_label">{t('login.resetPassword.giveUserAndEmail')}</span>
          {
            <Alert
              sx={{ fontSize: '12px', alignItems: 'center', marginTop: '20px', padding: '0px 10px 0px 10px' }}
              severity="warning"
              data-qa-element-id="must_use_itrack_username_and_email_alert"
            >
              {t('login.resetPassword.notification')}
            </Alert>
          }
        </div>
      )}

      <Box component="form" ref={ref} onSubmit={handleSubmit} noValidate>
        <FormControl
          variant="filled"
          margin="normal"
          className="login-page-user-pass-shrinked"
          fullWidth
          required
          error={state.errors.email !== undefined}
          data-qa-element-id="email_field"
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: 'var(--mui-shape-borderRadius)',
            '& .MuiFormHelperText-root': {
              backgroundColor: '#fff',
              marginLeft: 0,
              marginRight: 0,
              paddingLeft: '14px',
              paddingRight: '14px',
              borderEndEndRadius: '3px',
              borderEndStartRadius: '3px',
              boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
            },
            '& .Mui-error::before': {
              borderWidth: 'thick',
            },
          }}
        >
          <InputLabel
            sx={{ color: 'white', fontWeight: 'bold' }}
            htmlFor="email"
            style={{ color: 'white' }}
            shrink={true}
            data-qa-element-id="email_input_label"
          >
            {t('login.resetPassword.email')}
          </InputLabel>
          <FilledInput
            id="email"
            data-qa-element-id="email_input_field"
            className="login-page-user-pass-textfield"
            name="email"
            type="email"
            sx={{ color: 'white' }}
            endAdornment={
              <InputAdornment position="end" sx={{ padding: '8px' }} data-qa-element-id="email_input_icon">
                <AlternateEmail sx={{ color: 'white' }} fontSize="large" />
              </InputAdornment>
            }
          />

          <FormHelperText id="password-error-text" data-qa-element-id="email_error_text">
            {state.errors.email}
          </FormHelperText>
        </FormControl>

        <FormControl
          variant="filled"
          margin="normal"
          fullWidth
          required
          autoFocus
          error={state.errors.username !== undefined}
          data-qa-element-id="username_field"
          sx={{
            backgroundColor: 'primary.main',
            marginBottom: '21px',
            borderRadius: 'var(--mui-shape-borderRadius)',
            '& .MuiFormHelperText-root': {
              backgroundColor: '#fff',
              marginLeft: 0,
              marginRight: 0,
              paddingLeft: '14px',
              paddingRight: '14px',
              borderEndEndRadius: '3px',
              borderEndStartRadius: '3px',
              boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
            },
            '& .Mui-error::before': {
              borderWidth: 'thick',
            },
          }}
        >
          <InputLabel
            sx={{ color: 'white', fontWeight: 'bold' }}
            htmlFor="username"
            style={{ color: 'white' }}
            shrink={true}
            data-qa-element-id="username_input_label"
          >
            {t('login.username')}
          </InputLabel>
          <FilledInput
            id="username"
            data-qa-element-id="username_input_field"
            className="reset-password-page-user-textfield"
            name="username"
            type="username"
            sx={{ color: 'white' }}
            endAdornment={
              <InputAdornment position="end" sx={{ padding: '8px' }} data-qa-element-id="username_input_icon">
                <Person sx={{ color: 'white' }} fontSize="large" />
              </InputAdornment>
            }
          />
          <FormHelperText id="username-error-text" data-qa-element-id="username_error_text">
            {state.errors.username}
          </FormHelperText>
        </FormControl>

        <Submit />

        <Box className="reset-password-page-forgot-password" mt={2} mb={2}>
          <Grid container direction="row" justifyContent="right" alignItems="center">
            <Grid item xs={12} sm={7}>
              <Link
                component="button"
                onClick={returnLoginPage}
                underline="none"
                data-qa-element-id="return_to_login_button"
              >
                {t('login.resetPassword.back')}
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}

export function ResetPasswordForm({
  state,
  formAction,
  returnLoginPage,
  isPhone,
}: {
  state: State<ResetPasswordFields>;
  formAction: (payload: FormData) => void;
  returnLoginPage: () => void;
  isPhone: boolean;
}) {
  return (
    <FrontPage isPhone={isPhone}>
      <ResetPassword state={state} formAction={formAction} returnLoginPage={returnLoginPage} isPhone={isPhone} />
    </FrontPage>
  );
}
